import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { motion } from 'framer-motion';
import { 
  TreeDeciduous, 
  MapPin, 
  Calendar,
  Heart,
  Award,
  ArrowLeft,
  Image,
  Loader2,
  Share2,
  ExternalLink
} from 'lucide-react';

const API = `${import.meta.env.REACT_APP_BACKEND_URL}/api`;

export default function TreeDetailPage() {
  const { treeId } = useParams();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [tree, setTree] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (treeId) {
      fetchTreeDetails();
    }
  }, [treeId]);

  const fetchTreeDetails = async () => {
    try {
      const [treeRes, certRes] = await Promise.all([
        fetch(`${API}/trees/${treeId}`),
        fetch(`${API}/certificates/${treeId}`)
      ]);

      if (treeRes.ok) {
        const treeData = await treeRes.json();
        setTree(treeData);
      }
      
      if (certRes.ok) {
        const certData = await certRes.json();
        setCertificate(certData.certificate);
      }
    } catch (e) {
      console.error('Failed to fetch tree:', e);
    } finally {
      setLoading(false);
    }
  };

  const getSpeciesName = (code) => {
    const species = {
      lenga: 'Lenga Beech',
      nire: 'Ñire Beech',
      coihue: 'Coihue',
      cipres: 'Patagonian Cypress',
      araucaria: 'Monkey Puzzle'
    };
    return species[code] || code;
  };

  const getStatusColor = (status) => {
    const colors = {
      planned: 'bg-blue-500/10 text-blue-600',
      planted: 'bg-green-500/10 text-green-600',
      maintained: 'bg-green-500/10 text-green-600',
      dead: 'bg-red-500/10 text-red-600',
      replaced: 'bg-yellow-500/10 text-yellow-600'
    };
    return colors[status] || 'bg-muted text-muted-foreground';
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: `Tree ${tree?.tree_id} - Epuyén Renace`,
        text: 'Check out this tree in Patagonia!',
        url
      });
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background" data-testid="tree-detail-page">
        <Navbar />
        <main className="pt-24 pb-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary" />
            <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
          </div>
        </main>
      </div>
    );
  }

  if (!tree) {
    return (
      <div className="min-h-screen bg-background" data-testid="tree-detail-page">
        <Navbar />
        <main className="pt-24 pb-16 px-4">
          <div className="max-w-lg mx-auto text-center">
            <TreeDeciduous className="w-16 h-16 mx-auto text-muted-foreground mb-6" />
            <h1 className="text-2xl font-bold mb-4">Tree Not Found</h1>
            <Button asChild>
              <Link to="/map">Go to Map</Link>
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const isAdopted = !!tree.donor_user_id;

  return (
    <div className="min-h-screen bg-background" data-testid="tree-detail-page">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mb-6"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.back')}
          </Button>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <motion.div 
              className="lg:col-span-2 space-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Hero Card */}
              <Card className="overflow-hidden">
                <div className="h-64 bg-gradient-to-br from-primary/20 via-secondary/20 to-accent/20 flex items-center justify-center relative">
                  <TreeDeciduous className="w-32 h-32 text-primary/40" />
                  <Badge className={`absolute top-4 right-4 ${isAdopted ? 'bg-accent' : ''}`}>
                    {isAdopted ? t('tree.adopted') : t(`tree.statuses.${tree.status}`)}
                  </Badge>
                </div>
                <CardContent className="p-6">
                  <h1 className="text-3xl font-bold mb-2" data-testid="tree-species">
                    {getSpeciesName(tree.species)}
                  </h1>
                  <p className="font-mono text-muted-foreground">{tree.tree_id}</p>
                </CardContent>
              </Card>

              {/* Details Grid */}
              <div className="grid sm:grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{t('tree.coordinates')}</p>
                        <p className="font-mono text-sm">{tree.lat.toFixed(6)}, {tree.lng.toFixed(6)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getStatusColor(tree.status)}`}>
                        <TreeDeciduous className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{t('tree.status')}</p>
                        <p className="font-semibold capitalize">{t(`tree.statuses.${tree.status}`)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {tree.planting_date && (
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">{t('tree.plantingDate')}</p>
                          <p className="font-semibold">
                            {new Date(tree.planting_date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {isAdopted && (
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                          <Heart className="w-5 h-5 text-accent" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">{t('tree.adoptedBy')}</p>
                          <p className="font-semibold">{tree.donor_name || 'Anonymous'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Photos Section */}
              {tree.photos && tree.photos.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Image className="w-5 h-5" />
                      {t('tree.photos')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {tree.photos.map((photo, index) => (
                        <div 
                          key={index} 
                          className="aspect-square rounded-lg overflow-hidden bg-muted"
                        >
                          <img 
                            src={photo.url} 
                            alt={photo.caption || `Photo ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </motion.div>

            {/* Sidebar */}
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              {/* Certificate */}
              {certificate && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Award className="w-5 h-5 text-accent" />
                      {t('myForest.certificate')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="p-4 bg-muted rounded-lg border border-dashed">
                      <p className="text-xs text-muted-foreground mb-2">Certificate Hash</p>
                      <p className="font-mono text-xs break-all">{certificate.hash}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">
                      Issued: {new Date(certificate.issue_date).toLocaleDateString()}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Actions */}
              <Card>
                <CardContent className="p-4 space-y-3">
                  {!isAdopted && (
                    <Button asChild className="w-full btn-accent" data-testid="adopt-btn">
                      <Link to={`/adopt?tree=${tree.tree_id}`}>
                        <Heart className="w-4 h-4 mr-2" />
                        {t('tree.adoptThisTree')}
                      </Link>
                    </Button>
                  )}
                  
                  <Button asChild variant="outline" className="w-full">
                    <Link to={`/map?lat=${tree.lat}&lng=${tree.lng}`}>
                      <MapPin className="w-4 h-4 mr-2" />
                      View on Map
                    </Link>
                  </Button>

                  <Button variant="secondary" className="w-full" onClick={handleShare} data-testid="share-btn">
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>

                  <Button variant="ghost" className="w-full" asChild>
                    <a 
                      href={`https://www.google.com/maps?q=${tree.lat},${tree.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Google Maps
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
}
